import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CalendarPlus } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-[#0a0a0a] border-zinc-800 text-white h-10 rounded-sm placeholder:text-zinc-600";
const DURATIONS = [30, 45, 60, 90, 120, 180, 240];

export const ScheduleDialog = ({ open, onOpenChange, context, appointment, onSaved }) => {
  const isEdit = Boolean(appointment?.appointment_id);
  const src = appointment || context || {};
  const [form, setForm] = useState({
    name: src.name || "",
    phone: src.phone || "",
    date: appointment?.date || "",
    start_time: appointment?.start_time || "",
    duration_minutes: appointment?.duration_minutes || 60,
    service_type: appointment?.service_type || src.service_needed || "",
    technician_id: appointment?.technician_id || "",
    technician_name: appointment?.technician_name || "",
    technician_phone: appointment?.technician_phone || "",
    priority: appointment?.priority || "Normal",
    address: appointment?.address || src.address || "",
    notes: appointment?.notes || "",
  });
  const [techs, setTechs] = useState([]);
  const [remindersEnabled, setRemindersEnabled] = useState(appointment?.reminders_enabled ?? true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/techs`).then((res) => setTechs(res.data.filter((t) => t.active))).catch(() => {});
  }, []);

  const selectTech = (techId) => {
    const t = techs.find((x) => x.tech_id === techId);
    setForm((f) => ({
      ...f, technician_id: techId,
      technician_name: t ? t.name : "",
      technician_phone: t ? t.phone : "",
    }));
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (force = false) => {
    if (!form.name || !form.phone || !form.date || !form.start_time || !form.service_type) {
      toast.error("Name, phone, date, time, and service are required.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      duration_minutes: Number(form.duration_minutes),
      reminders_enabled: remindersEnabled,
      origin_url: window.location.origin,
      force,
    };
    try {
      let res;
      if (isEdit) {
        res = await axios.patch(`${API}/admin/appointments/${appointment.appointment_id}`, payload);
      } else {
        res = await axios.post(`${API}/admin/appointments`, {
          ...payload,
          lead_id: context?.lead_id || null,
          customer_id: context?.customer_id || null,
        });
      }
      toast.success(isEdit ? "Appointment updated" : "Appointment scheduled");
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (err) {
      if (err.response?.status === 409) {
        if (window.confirm(`${err.response.data.detail}\n\nBook anyway?`)) {
          await save(true);
          return;
        }
      } else {
        toast.error(err.response?.data?.detail || "Could not save appointment");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CalendarPlus className="h-5 w-5 text-blue-500" />
            {isEdit ? "Edit / Reschedule Appointment" : "Schedule Appointment"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-zinc-400 text-xs">Name *</Label>
            <Input data-testid="schedule-input-name" className={inputCls} value={form.name} onChange={set("name")} />
          </div>
          <div className="space-y-1">
            <Label className="text-zinc-400 text-xs">Phone *</Label>
            <Input data-testid="schedule-input-phone" className={inputCls} value={form.phone} onChange={set("phone")} />
          </div>
          <div className="space-y-1">
            <Label className="text-zinc-400 text-xs">Date *</Label>
            <Input data-testid="schedule-input-date" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.date} onChange={set("date")} />
          </div>
          <div className="space-y-1">
            <Label className="text-zinc-400 text-xs">Start Time *</Label>
            <Input data-testid="schedule-input-time" type="time" className={`${inputCls} [color-scheme:dark]`} value={form.start_time} onChange={set("start_time")} />
          </div>
          <div className="space-y-1">
            <Label className="text-zinc-400 text-xs">Estimated Duration</Label>
            <select
              data-testid="schedule-select-duration"
              className="w-full h-10 bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-3 text-sm"
              value={form.duration_minutes}
              onChange={set("duration_minutes")}
            >
              {DURATIONS.map((d) => <option key={d} value={d}>{d} minutes</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-zinc-400 text-xs">Service Type *</Label>
            <Input data-testid="schedule-input-service" className={inputCls} value={form.service_type} onChange={set("service_type")} />
          </div>
          <div className="space-y-1">
            <Label className="text-zinc-400 text-xs">Assigned Technician</Label>
            {techs.length > 0 ? (
              <select
                data-testid="schedule-select-tech"
                className="w-full h-10 bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-3 text-sm"
                value={form.technician_id}
                onChange={(e) => selectTech(e.target.value)}
              >
                <option value="">Unassigned</option>
                {techs.map((t) => <option key={t.tech_id} value={t.tech_id}>{t.name}</option>)}
              </select>
            ) : (
              <Input data-testid="schedule-input-tech-name" className={inputCls} placeholder="Tech name (add techs in Techs tab)" value={form.technician_name} onChange={set("technician_name")} />
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-zinc-400 text-xs">Priority</Label>
            <select
              data-testid="schedule-select-priority"
              className="w-full h-10 bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-3 text-sm"
              value={form.priority}
              onChange={set("priority")}
            >
              <option>Normal</option><option>High</option><option>Emergency</option>
            </select>
          </div>
          <div className="col-span-2 space-y-1">
            <Label className="text-zinc-400 text-xs">Technician Phone (gets reminders)</Label>
            <Input data-testid="schedule-input-tech-phone" className={inputCls} placeholder="(702) 555-0100" value={form.technician_phone} onChange={set("technician_phone")} />
          </div>
          <div className="col-span-2 space-y-1">
            <Label className="text-zinc-400 text-xs">Service Address / Location</Label>
            <Input data-testid="schedule-input-address" className={inputCls} value={form.address} onChange={set("address")} />
          </div>
          <div className="col-span-2 space-y-1">
            <Label className="text-zinc-400 text-xs">Appointment Notes</Label>
            <Input data-testid="schedule-input-notes" className={inputCls} value={form.notes} onChange={set("notes")} />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
          <Checkbox data-testid="schedule-reminders-toggle" checked={remindersEnabled} onCheckedChange={setRemindersEnabled} />
          Automatic text confirmations &amp; reminders (immediate, 24h and 2h before — customer + assigned tech)
        </label>
        <DialogFooter>
          <button
            data-testid="schedule-save-button"
            onClick={() => save(false)}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-sm px-6 py-2.5 font-semibold transition-colors"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? "Save Changes" : "Schedule Appointment"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
